/**
 * VAIU PoS ingress parsing (see vaiu-nfc/shared/docs/VAIU-POS-EXCHANGE.md).
 */

export const VAIU_POS_SCHEMA_VERSION = 1 as const;

export type VaiuPosType = 'invoice' | 'basket';

export interface VaiuPosInvoiceV1 {
  v: typeof VAIU_POS_SCHEMA_VERSION;
  type: 'invoice';
  invoiceTokenKey: string;
  merchantId?: string;
  sessionId?: string;
}

export interface VaiuPosBasketLineV1 {
  sku?: string;
  name: string;
  qty: number;
  unitPrice: number;
}

export interface VaiuPosBasketV1 {
  v: typeof VAIU_POS_SCHEMA_VERSION;
  type: 'basket';
  amount: number;
  currency: string;
  basket?: VaiuPosBasketLineV1[];
  posSessionId?: string;
  message?: string;
  merchantId?: string;
  signature?: string;
}

export type VaiuPosPayloadV1 = VaiuPosInvoiceV1 | VaiuPosBasketV1;

export type ParsePoSResult =
  | { ok: true; payload: VaiuPosPayloadV1 }
  | { ok: false; error: string };

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** Decode base64url (no padding) to UTF-8 string. */
export function decodeBase64UrlData(data: string): string | null {
  try {
    let s = data.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    const Buf = (globalThis as unknown as { Buffer?: { from: (x: string, enc: string) => { toString: (enc: string) => string } } }).Buffer;
    if (Buf) {
      return Buf.from(s, 'base64').toString('utf8');
    }
    if (typeof atob !== 'undefined') {
      const bin = atob(s);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new TextDecoder().decode(bytes);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parse JSON document from NDEF text, deep-link body, or QR.
 */
export function parseVaiuPosJson(raw: string): ParsePoSResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, error: 'empty_payload' };

  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'invalid_json' };
  }

  if (!isRecord(obj)) return { ok: false, error: 'not_object' };

  const v = obj.v;
  const type = obj.type;

  if (v !== 1) return { ok: false, error: 'unsupported_schema_version' };
  if (type !== 'invoice' && type !== 'basket') return { ok: false, error: 'invalid_type' };

  if (type === 'invoice') {
    const key = obj.invoiceTokenKey;
    if (typeof key !== 'string' || !key.trim()) {
      return { ok: false, error: 'missing_invoiceTokenKey' };
    }
    const payload: VaiuPosInvoiceV1 = {
      v: 1,
      type: 'invoice',
      invoiceTokenKey: key.trim(),
    };
    if (typeof obj.merchantId === 'string') payload.merchantId = obj.merchantId;
    if (typeof obj.sessionId === 'string') payload.sessionId = obj.sessionId;
    return { ok: true, payload };
  }

  const amount = obj.amount;
  const currency = obj.currency;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
    return { ok: false, error: 'invalid_amount' };
  }
  if (typeof currency !== 'string' || !currency.trim()) {
    return { ok: false, error: 'missing_currency' };
  }

  const payload: VaiuPosBasketV1 = {
    v: 1,
    type: 'basket',
    amount,
    currency: currency.trim().toUpperCase(),
  };

  if (typeof obj.posSessionId === 'string') payload.posSessionId = obj.posSessionId;
  if (typeof obj.message === 'string') payload.message = obj.message;
  if (typeof obj.merchantId === 'string') payload.merchantId = obj.merchantId;
  if (typeof obj.signature === 'string') payload.signature = obj.signature;

  if (Array.isArray(obj.basket)) {
    const lines: VaiuPosBasketLineV1[] = [];
    for (const row of obj.basket) {
      if (!isRecord(row)) continue;
      const name = row.name;
      const qty = row.qty;
      const unitPrice = row.unitPrice;
      if (typeof name !== 'string' || typeof qty !== 'number' || typeof unitPrice !== 'number') continue;
      const line: VaiuPosBasketLineV1 = { name, qty, unitPrice };
      if (typeof row.sku === 'string') line.sku = row.sku;
      lines.push(line);
    }
    if (lines.length) payload.basket = lines;
  }

  return { ok: true, payload };
}

/** If string is a vaiu HTTPS / pos URL with data=, extract and parse. */
export function parseVaiuPosFromUriString(raw: string): ParsePoSResult | null {
  const t = raw.trim();
  if (!t.startsWith('http://') && !t.startsWith('https://') && !t.startsWith('vaiu://')) {
    return null;
  }
  try {
    const u = new URL(t);
    const data = u.searchParams.get('data');
    if (data) {
      const json = decodeBase64UrlData(data);
      if (!json) return { ok: false, error: 'bad_base64_data' };
      return parseVaiuPosJson(json);
    }
    return parseFromSearchParams(u.searchParams);
  } catch {
    return { ok: false, error: 'bad_url' };
  }
}

function parseFromSearchParams(params: URLSearchParams): ParsePoSResult {
  const type = params.get('type');
  if (type === 'invoice') {
    const key = params.get('invoiceTokenKey') || params.get('key');
    if (!key) return { ok: false, error: 'missing_invoiceTokenKey' };
    const payload: VaiuPosInvoiceV1 = {
      v: 1,
      type: 'invoice',
      invoiceTokenKey: key,
    };
    const mid = params.get('merchantId');
    const sid = params.get('sessionId');
    if (mid) payload.merchantId = mid;
    if (sid) payload.sessionId = sid;
    return { ok: true, payload };
  }
  if (type === 'basket') {
    const amount = parseFloat(params.get('amount') || '');
    const currency = params.get('currency') || '';
    if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: 'invalid_amount' };
    if (!currency.trim()) return { ok: false, error: 'missing_currency' };
    const payload: VaiuPosBasketV1 = {
      v: 1,
      type: 'basket',
      amount,
      currency: currency.trim().toUpperCase(),
    };
    const ps = params.get('posSessionId');
    const msg = params.get('message');
    const mid = params.get('merchantId');
    if (ps) payload.posSessionId = ps;
    if (msg) payload.message = msg;
    if (mid) payload.merchantId = mid;
    return { ok: true, payload };
  }
  return { ok: false, error: 'missing_type' };
}

/**
 * Parse ingress from raw NFC / paste / single string (JSON or URI).
 */
export function parseVaiuPosIngress(raw: string): ParsePoSResult {
  const uri = parseVaiuPosFromUriString(raw);
  if (uri) return uri;
  return parseVaiuPosJson(raw);
}

/**
 * Parse from `/pos` location search string (leading `?` optional).
 * Returns `null` when there is no PoS-related query (empty or unrelated params).
 */
export function tryParseVaiuPosFromLocationSearch(search: string): ParsePoSResult | null {
  const q = search.startsWith('?') ? search.slice(1) : search;
  if (!q.trim()) return null;
  const params = new URLSearchParams(q);
  if (!params.has('data') && !params.has('type')) return null;
  const data = params.get('data');
  if (data) {
    const json = decodeBase64UrlData(data);
    if (!json) return { ok: false, error: 'bad_base64_data' };
    return parseVaiuPosJson(json);
  }
  return parseFromSearchParams(params);
}

/** Human summary line for basket (for token message fallback). */
export function basketSummaryMessage(payload: VaiuPosBasketV1): string {
  if (payload.message?.trim()) return payload.message.trim();
  const n = payload.basket?.length ?? 0;
  if (n > 0) return `PoS checkout (${n} line${n === 1 ? '' : 's'})`;
  return 'PoS checkout';
}
